import { YardPropertiesService } from './../../../../../yard-properties.service';
import { TMCClientParameters } from '@sdbn/tmc-common';
import { WeighingType } from './../model/weighing-type.enum';
import { NgModel, NgForm } from '@angular/forms';
import { EntranceRegistration } from './../model/out/entrance-registration';
import { TransportFormatterService } from '../../../../../legacy/transport-formatter-service';
import { Component, Inject, ViewChild } from '@angular/core';
import { Transport } from '../model/Transport';
import { RegisterEntranceService } from '../service/register-entrance.service';
import { Check } from '../model/Check';
import { CheckState } from '../model/CheckState.enum';
import { TranslationService } from '@sdbn/common';
import { AppClientConfigurationService } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/common';
import { ContextChange } from '@sdbn/common';
import { Observable } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AutoWeighingService } from '../../../../../legacy/auto-weighing/auto-weighing.service';
import { AreaGate } from '../model/areaGate';

interface IConfirmCallback {
  ();
}

export interface RegisterEntranceDialogData {
  transportId: number;
  callback: IConfirmCallback;
}

@Component({
  selector: 'app-register-entrance',
  templateUrl: './register-entrance.component.html',
  styleUrls: ['./register-entrance.component.scss']
})
export class RegisterEntranceComponent extends AbstractWidgetComponent {

  @ViewChild('weighForm') weighForm: NgForm;

  transport = new Transport();
  isLoading = false;
  identifier: string;

  clientParameters = TMCClientParameters;
  typeOptions = new Array<WeighingType>();

  areaGates = new Array<AreaGate>();
  selectedAreaGate: AreaGate = null;

  constructor(
    private dialogRef: MatDialogRef<RegisterEntranceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RegisterEntranceDialogData,
    private entranceService: RegisterEntranceService,
    public cps: AppClientConfigurationService,
    public translationService: TranslationService,
    private transportFormatterService: TransportFormatterService,
    private autoWeighingService: AutoWeighingService,
    public yardPropertiesService: YardPropertiesService) {

    super();

    for (const t of Object.keys(WeighingType)) {
      this.typeOptions.push(WeighingType[t]);
    }

    if (this.data?.transportId != null) {
      this.loadTransport(this.data.transportId);
    }

    this.loadAreaGates();

  }

  onContextChange(contextChange: ContextChange) {
    if (contextChange.key === 'selected-transport') {
      const transportId = this.dashboardContext.getContextValue('selected-transport')?.id;
      if (transportId != null) {
        this.loadTransport(transportId);
      }
    }
  }

  onSetElementAndContext() {
    const transportId = this.dashboardContext.getContextValue('selected-transport')?.id;
    if (transportId != null) {
      this.loadTransport(transportId);
    }
  }

  private loadAreaGates() {
    this.entranceService.getAreaGates().subscribe(gates => {
      this.areaGates = gates;
      if (this.areaGates.length > 0) {
        this.selectedAreaGate = this.areaGates[0];
      }
    });
  }

  private loadTransport(transportId: number) {
    this.entranceService.getTransport({ id: transportId }).subscribe(
      response => {
        this.transport = response;
        this.identifier = this.formatIdentifier(this.transport);
        this.transport.autoWeightSource = this.yardPropertiesService.getEntranceType();

        if (this.transport.autoWeightSource != null && this.transport.autoWeightSource.length === 0) {
          this.transport.autoWeightSource = null;
        }

        const defaultType = this.yardPropertiesService.getEntranceScaleKey();
        if (defaultType != null && defaultType.length > 0) {
          this.transport.weighingType = WeighingType[defaultType];
        }

      }, error => {
        console.error(error);
      });
  }

  isOk(check: Check) {
    return check.state === CheckState.OK;
  }

  isWarning(check: Check) {
    return check.state === CheckState.WARN;
  }

  isFailed(check: Check) {
    return check.state === CheckState.FAILED;
  }

  isInfo(check: Check) {
    return check.state === CheckState.INFO;
  }

  checksExist(transport: Transport): boolean {
    return transport != null && transport.checks != null && transport.checks.length > 0;
  }

  cancel() {
    this.dialogRef.close();
  }

  decline() {

    this.isLoading = true;

    const er = new EntranceRegistration();
    er.transportId = this.transport.id;
    er.weighingType = this.transport.weighingType;
    er.weight = this.transport.weight;
    er.areaGateId = this.selectedAreaGate?.id;

    this.entranceService.decline(er).subscribe(
      response => {
        this.dialogRef.close();
        this.data.callback();
        this.isLoading = false;
      }, error => {
        console.error(error);
        this.isLoading = false;
      }
    );
  }

  isDeclineDisabled() {
    if (this.isLoading) {
      return true;
    }

    return false;
  }

  onAcceptTabbed() {
    this.isLoading = true;

    this.accept().subscribe(
      response => {
        this.dialogRef.close();
        this.data.callback();
        this.isLoading = false;
      }, error => {
        console.error(error);
        this.isLoading = false;
      }
    );
  }

  public accept(): Observable<any> {

    const er = new EntranceRegistration();
    er.transportId = this.transport.id;
    er.weighingType = this.transport.weighingType;
    er.weight = this.transport.weight;
    er.areaGateId = this.selectedAreaGate?.id;

    return this.entranceService.accept(er);
  }

  isAcceptDisabled() {
    if (this.isLoading) {
      return true;
    }

    if (this.isWeighingActive()) {
      if (this.weighForm && this.weighForm.invalid) {
        return true;
      }
    }

    return !this.transport.allowEntrance;
  }

  isWeighingTypeRequired(): boolean {
    return this.transport.weight != null && this.transport.weight > 0;
  }

  descriptionExists(check: Check) {
    if (!check.descriptionDictionaryKey) {
      return false;
    }
    const description: string = this.translationService.translate(check.descriptionDictionaryKey);
    return description.length > 0;
  }

  private formatIdentifier(transport: Transport) {
    return this.transportFormatterService.formatTransportIdentifier(transport.identifier, transport.transportScope);
  }

  showValuePositiveIntegerError(ctrl: NgModel) {
    return ctrl.touched && ctrl.hasError('positiveInteger');
  }

  public isWeighingActive(): boolean {
    return this.yardPropertiesService.isWeighingEntranceEnabled() &&
      this.yardPropertiesService.isWeighingEnabled();
  }

  public readWeight() {
    this.autoWeighingService.readWeight(this.transport.autoWeightSource).subscribe(result => {
      this.transport.autoWeight = result.weight;
      this.transport.weight = this.transport.autoWeight;
    });
  }

  public isAutoWeighingVisible(): boolean {
    return this.transport != null && this.transport.autoWeightSource != null && this.transport.autoWeightSource.length > 0;
  }
}
